'use client';

/**
 * Architecture Docs Widget
 *
 * Six view modes:
 * - Graph: Force-directed network visualization (Reagraph)
 * - Radial: Circular layout with system at center (xyflow)
 * - Value: Buyer-focused view with story modes (xyflow)
 * - Flow: Left-to-right pipeline showing data movement (xyflow)
 * - Capability: Semantic view - packages hidden, capabilities promoted (Pixi.js WebGL)
 * - Tree: Hierarchical tree exploration (ArchExplorer)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '@/lib/api/client';
import {
  Loader2,
  Network,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Package,
  Target,
  Layers,
  BookOpen,
  ListTree,
  Focus,
  X,
  CircleDot,
  Sparkles,
  Workflow,
  Briefcase,
  Zap,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { ArchExplorer } from '@/components/explorer/arch-explorer';
// TODO: Implement these widgets
// import { ArchRadialWidget } from '@/components/widgets/arch-radial-widget';
// import { ArchValueWidget } from '@/components/widgets/arch-value-widget';
import { ArchFlowWidget } from '@/components/widgets/arch-flow-widget';
import { ArchFlowPixi } from '@/components/widgets/arch-flow-pixi';
import { ArchCapabilityPixi } from '@/components/widgets/arch-capability-pixi';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// Types
interface ArchNode {
  id: string;
  label: string;
  type: string;
  description?: string;
  extra?: { story_count?: number };
}

interface ArchEdge {
  id: string;
  source: string;
  target: string;
  type: string;
}

interface ArchData {
  nodes: ArchNode[];
  edges: ArchEdge[];
  metadata: {
    node_count: number;
    edge_count: number;
    packages: number;
    capabilities: number;
    features: number;
    stories: number;
  };
}

// Colors by type
const NODE_COLORS: Record<string, string> = {
  system: '#F5A623',
  package: '#BD10E0',
  capability: '#4DD0E1',
  feature: '#50E3C2',
  story: '#7B68EE',
};

const NODE_SIZES: Record<string, number> = {
  system: 20,
  package: 16,
  capability: 14,
  feature: 10,
  story: 6,
};

const EDGE_COLORS: Record<string, string> = {
  HAS_PACKAGE: '#BD10E0',
  PROVIDES: '#4DD0E1',
  HAS_FEATURE: '#50E3C2',
  HAS_STORY: '#7B68EE',
  default: '#666666',
};

const TYPE_ICONS: Record<string, typeof Package> = {
  system: Network,
  package: Package,
  capability: Target,
  feature: Layers,
  story: BookOpen,
};

type ViewMode = 'graph' | 'radial' | 'value' | 'flow' | 'flow-pixi' | 'capability' | 'tree';

/**
 * Get lineage (ancestors + descendants) for a focused node.
 * Used for focus mode filtering.
 */
function getLineage(
  focusedId: string,
  nodes: ArchNode[],
  edges: ArchEdge[]
): Set<string> {
  const lineage = new Set<string>();
  lineage.add(focusedId);

  // Build adjacency maps
  const children = new Map<string, string[]>(); // parent -> children
  const parents = new Map<string, string>();    // child -> parent

  for (const edge of edges) {
    if (!children.has(edge.source)) {
      children.set(edge.source, []);
    }
    children.get(edge.source)!.push(edge.target);
    parents.set(edge.target, edge.source);
  }

  // Traverse up (ancestors)
  let current = focusedId;
  while (parents.has(current)) {
    current = parents.get(current)!;
    lineage.add(current);
  }

  // Traverse down (descendants) using BFS
  const queue = [focusedId];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const nodeChildren = children.get(nodeId) || [];
    for (const childId of nodeChildren) {
      if (!lineage.has(childId)) {
        lineage.add(childId);
        queue.push(childId);
      }
    }
  }

  return lineage;
}

interface ArchDocsWidgetProps {
  includeStories?: boolean;
}

export function ArchDocsWidget({ includeStories = true }: ArchDocsWidgetProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);
  const { resolvedTheme } = useTheme();
  const graphRef = useRef<any>(null);
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Data state
  const [data, setData] = useState<ArchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);
  const [clusteringEnabled, setClusteringEnabled] = useState(true);

  // Focus mode state - when set, filters graph to show only lineage
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  // Load ReaGraph only on client
  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);

      // Customize themes
      const customDarkTheme = {
        ...mod.darkTheme,
        canvas: { ...mod.darkTheme.canvas, background: '#121212' },
        node: { ...mod.darkTheme.node, activeFill: '#ffffff', inactiveOpacity: 0.3 },
        edge: { ...mod.darkTheme.edge, activeFill: '#ffffff', inactiveOpacity: 0.2 },
      };
      const customLightTheme = {
        ...mod.lightTheme,
        canvas: { ...mod.lightTheme.canvas, background: '#ffffff' },
        node: { ...mod.lightTheme.node, activeFill: '#000000', inactiveOpacity: 0.3 },
        edge: { ...mod.lightTheme.edge, activeFill: '#000000', inactiveOpacity: 0.2 },
      };
      setThemes({ dark: customDarkTheme, light: customLightTheme });
    });
  }, []);

  // Fetch data
  useEffect(() => {
    if (!isClient) return;

    const fetchData = async () => {
      try {
        const params = new URLSearchParams();
        if (includeStories) params.set('include_stories', 'true');

        const result = await api.get<ArchData>(
          `/docs/arch/canvas${params.toString() ? '?' + params.toString() : ''}`
        );
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isClient, includeStories]);

  // Transform to reagraph format
  const { nodes, edges } = useMemo(() => {
    if (!data?.nodes) return { nodes: [], edges: [] };

    const nodes = data.nodes.map((node) => ({
      id: node.id,
      label: node.label,
      fill: NODE_COLORS[node.type] || '#8AB4F8',
      size: NODE_SIZES[node.type] || 8,
      cluster: node.type,
      data: {
        type: node.type,
        description: node.description,
        storyCount: node.extra?.story_count,
      },
    }));

    const edges = data.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.type?.replace(/_/g, ' ') || '',
      fill: EDGE_COLORS[edge.type] || EDGE_COLORS.default,
    }));

    return { nodes, edges };
  }, [data]);

  // Filter nodes/edges based on focus mode
  // - No focus: Show L1-L3 (hide stories for cleaner initial view)
  // - Focused: Show full lineage including stories
  const { visibleNodes, visibleEdges } = useMemo(() => {
    if (!data || !nodes.length) {
      return { visibleNodes: [], visibleEdges: [] };
    }

    if (!focusedNodeId) {
      // Initial view: L1-L3 only (no stories)
      const filteredNodes = nodes.filter((n) => n.data?.type !== 'story');
      const filteredNodeIds = new Set(filteredNodes.map((n) => n.id));
      const filteredEdges = edges.filter(
        (e) => filteredNodeIds.has(e.source) && filteredNodeIds.has(e.target)
      );
      return { visibleNodes: filteredNodes, visibleEdges: filteredEdges };
    }

    // Focused view: full lineage INCLUDING stories
    const lineage = getLineage(focusedNodeId, data.nodes, data.edges);
    const filteredNodes = nodes.filter((n) => lineage.has(n.id));
    const filteredEdges = edges.filter(
      (e) => lineage.has(e.source) && lineage.has(e.target)
    );
    return { visibleNodes: filteredNodes, visibleEdges: filteredEdges };
  }, [focusedNodeId, nodes, edges, data]);

  // Get focused node label for display
  const focusedNodeLabel = useMemo(() => {
    if (!focusedNodeId || !data) return null;
    const node = data.nodes.find((n) => n.id === focusedNodeId);
    return node?.label || null;
  }, [focusedNodeId, data]);

  // Handlers
  const handleNodeClick = useCallback((node: any) => {
    setSelectedNodes([node.id]);

    // Find the original node data to get type and description
    const archNode = data?.nodes.find((n) => n.id === node.id);
    if (archNode) {
      // Update copilot panel with selection
      selectNode({
        id: archNode.id,
        label: archNode.label,
        type: archNode.type,
        properties: {
          description: archNode.description,
          // Include story-specific properties if present
          ...(archNode.extra || {}),
        },
      });

      // Focus mode: only capabilities can be focused
      if (archNode.type === 'capability') {
        if (focusedNodeId === node.id) {
          // Toggle off if clicking same node
          setFocusedNodeId(null);
        } else {
          setFocusedNodeId(node.id);
        }
      }
    }
  }, [data, selectNode, focusedNodeId]);

  // Reset focus mode
  const handleResetFocus = useCallback(() => {
    setFocusedNodeId(null);
  }, []);

  const handleNodePointerOver = useCallback((node: any) => {
    setActiveNodes([node.id]);
  }, []);

  const handleNodePointerOut = useCallback(() => {
    setActiveNodes([]);
  }, []);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    graphRef.current?.zoomIn?.();
  }, []);

  const handleZoomOut = useCallback(() => {
    graphRef.current?.zoomOut?.();
  }, []);

  const handleFitView = useCallback(() => {
    graphRef.current?.fitNodesInView?.();
  }, []);

  // Loading states
  if (!isClient || loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading architecture...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <Network className="w-12 h-12 mb-4" />
        <p>Failed to load architecture</p>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </div>
    );
  }

  if (!data?.nodes.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-50" />
        <p>No architecture data</p>
        <p className="text-sm mt-2">Run sync to populate the graph</p>
      </div>
    );
  }

  if (!GraphCanvas) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading graph renderer...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-panel-border bg-panel">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Architecture</span>
          {/* Focus mode indicator */}
          {focusedNodeId && focusedNodeLabel && (
            <div className="flex items-center gap-1.5 ml-2 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
              <Focus className="w-3 h-3" />
              <span className="max-w-[120px] truncate">{focusedNodeLabel}</span>
              <button
                onClick={handleResetFocus}
                className="p-0.5 hover:bg-primary/20 rounded-full transition-colors"
                title="Reset focus"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-muted/50 rounded-md p-0.5">
            <button
              onClick={() => setViewMode('graph')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'graph'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Network className="w-3.5 h-3.5" />
              Graph
            </button>
            <button
              onClick={() => setViewMode('radial')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'radial'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <CircleDot className="w-3.5 h-3.5" />
              Radial
            </button>
            <button
              onClick={() => setViewMode('value')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'value'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Value
            </button>
            <button
              onClick={() => setViewMode('flow')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'flow'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Workflow className="w-3.5 h-3.5" />
              Flow
            </button>
            <button
              onClick={() => setViewMode('flow-pixi')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'flow-pixi'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Zap className="w-3.5 h-3.5" />
              Flow+
            </button>
            <button
              onClick={() => setViewMode('capability')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'capability'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              title="Semantic view - packages hidden"
            >
              <Briefcase className="w-3.5 h-3.5" />
              Capability
            </button>
            <button
              onClick={() => setViewMode('tree')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'tree'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <ListTree className="w-3.5 h-3.5" />
              Tree
            </button>
          </div>

          {/* Graph-specific controls */}
          {viewMode === 'graph' && (
            <>
              {/* Clustering toggle */}
              <button
                onClick={() => setClusteringEnabled(!clusteringEnabled)}
                className={cn(
                  'p-1.5 rounded text-xs transition-colors',
                  clusteringEnabled
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted'
                )}
                title="Toggle clustering"
              >
                Cluster
              </button>

              {/* Zoom controls */}
              <div className="flex items-center border-l border-panel-border pl-2 ml-1">
                <button onClick={handleZoomIn} className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom in">
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button onClick={handleZoomOut} className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom out">
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button onClick={handleFitView} className="p-1.5 rounded hover:bg-muted transition-colors" title="Fit to view">
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground border-l border-panel-border pl-2 ml-1">
            <span>{data?.metadata.packages}p</span>
            <span>{data?.metadata.capabilities}c</span>
            <span>{data?.metadata.features}f</span>
            <span>{data?.metadata.stories}s</span>
          </div>
        </div>
      </div>

      {/* Content - Graph, Radial, Value, or Tree */}
      {viewMode === 'tree' ? (
        <div className="flex-1 overflow-hidden">
          <ArchExplorer />
        </div>
      ) : viewMode === 'radial' && data ? (
        <div className="flex-1 bg-background relative overflow-hidden flex items-center justify-center text-muted-foreground" style={{ minHeight: '400px' }}>
          Radial View (Coming Soon)
        </div>
      ) : viewMode === 'value' && data ? (
        <div className="flex-1 bg-background relative overflow-hidden flex items-center justify-center text-muted-foreground" style={{ minHeight: '400px' }}>
          Value View (Coming Soon)
        </div>
      ) : viewMode === 'flow' ? (
        <div className="flex-1 bg-background relative overflow-hidden" style={{ minHeight: '400px' }}>
          <ArchFlowWidget />
        </div>
      ) : viewMode === 'flow-pixi' ? (
        <div className="flex-1 bg-background relative overflow-hidden" style={{ minHeight: '400px' }}>
          <ArchFlowPixi />
        </div>
      ) : viewMode === 'capability' && data ? (
        <div className="flex-1 bg-background relative overflow-hidden" style={{ minHeight: '400px' }}>
          <ArchCapabilityPixi />
        </div>
      ) : (
        <>
          {/* Graph Container */}
          <div
            className="flex-1 bg-background relative overflow-hidden"
            style={{ minHeight: '400px' }}
            suppressHydrationWarning
          >
            <div className="absolute inset-0">
              <GraphCanvas
                ref={graphRef}
                nodes={visibleNodes}
                edges={visibleEdges}
                layoutType="forceDirected2d"
                clusterAttribute={clusteringEnabled ? 'cluster' : undefined}
                labelType="nodes"
                draggable
                animated
                cameraMode="pan"
                edgeArrowPosition="end"
                edgeLabelPosition="above"
                theme={themes ? (resolvedTheme === 'dark' ? themes.dark : themes.light) : undefined}
                selections={selectedNodes}
                actives={activeNodes}
                onNodeClick={handleNodeClick}
                onNodePointerOver={handleNodePointerOver}
                onNodePointerOut={handleNodePointerOut}
              />
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-panel-border bg-panel text-xs">
            <div className="flex items-center gap-4">
              {Object.entries(NODE_COLORS).map(([type, color]) => {
                const Icon = TYPE_ICONS[type] || Layers;
                return (
                  <div key={type} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                    <Icon className="w-3 h-3" style={{ color }} />
                    <span className="text-muted-foreground capitalize">{type}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
